/**
 * Resume pagination policy — single source of truth.
 *
 * A resume is fragmented into pages twice, by two different engines:
 *
 *   1. Export  — Chrome (in the PDF lambda) fragments the document natively,
 *                driven purely by the CSS `break-*` properties below.
 *   2. Preview — `ResumeDocumentPreview` slices one tall render into A4 windows,
 *                and reads these same properties back off the DOM via
 *                `getComputedStyle()`.
 *
 * Because the preview *reads* the policy instead of restating it, there is
 * nothing to keep in sync: a template declares `break-inside: avoid` on a block
 * and both engines honour it. Adding a template requires no change here.
 *
 * IMPORTANT: these rules must stay OUTSIDE `@media print`. The preview measures
 * in screen media, so a rule scoped to print reports `break-inside: auto` to
 * `getComputedStyle()` and the preview would paginate blind — which is exactly
 * how `focus-sidebar` ended up slicing headings in half. The `break-*`
 * properties have no visual effect on screen, so declaring them unconditionally
 * is free.
 */

/**
 * Leaf blocks that must never straddle a page boundary.
 *
 * Only ever list blocks whose height is bounded by their own content — a single
 * job entry, one education line, a skills group. Never list a *section*
 * container: sections grow with the content they hold, and once one exceeds a
 * page height Chrome relocates the whole thing to the next page before giving
 * up and splitting it anyway, stranding most of a page as whitespace.
 */
const ATOMIC_BLOCKS = [
  '.entry',
  '.entry-header',
  '.skill-category',
  '.simple-block',
  '.sidebar-section',
  '.identity',
  '.links',
  '.contact-info',
];

/**
 * Headings that must stay with the content that follows them, so a section
 * title never ends up alone at the foot of a page.
 *
 * Caveat: Chrome ignores `break-after: avoid` when fragmenting for print, so
 * this is honoured by the preview but not by the PDF export. The usual
 * workaround — extending the heading's padding box down over the content that
 * follows — is not available here: four of the templates decorate headings with
 * `border-bottom` + `padding-bottom`, and overriding that padding drags every
 * heading underline down with it. Fixing this properly needs the heading and
 * its first block wrapped in a single element at the template level.
 */
const KEEP_WITH_NEXT = ['h1', 'h2', 'h3', '.section-title', '.skill-category-name'];

/**
 * Section containers, which must remain splittable. Listed explicitly so a
 * template that sets `break-inside: avoid` on one of these is corrected rather
 * than silently reintroducing the blank-page bug.
 */
const SPLITTABLE_CONTAINERS = ['.section', '.main-section', '.timeline', '.right-pane', '.left-pane'];

export const RESUME_PAGINATION_CSS = `
    /* --- pagination policy (shared; see lib/resume/templates/pagination.ts) --- */
    ${ATOMIC_BLOCKS.join(',\n    ')} {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    ${SPLITTABLE_CONTAINERS.join(',\n    ')} {
      break-inside: auto;
      page-break-inside: auto;
    }

    ${KEEP_WITH_NEXT.join(',\n    ')} {
      break-after: avoid;
      page-break-after: avoid;
    }

`;

/* ------------------------------------------------------------------------- *
 * Reading the policy back: turning a rendered document into page offsets.
 * ------------------------------------------------------------------------- */

/** Sub-pixel slack, so a break sitting exactly on an edge is not "inside" it. */
const PAGE_EPSILON_PX = 2;
/** A break can be nudged up several times (out of a block, off a line, above a
 *  heading). Bounded so a pathological document cannot spin here. */
const MAX_LEGALIZE_PASSES = 8;
/** A page must advance by at least this much, or we take the straight cut. */
const MIN_BREAK_ADVANCE_PX = 16;
/** Below this, the remainder is not worth a page of its own. */
const MIN_LAST_PAGE_CONTENT_PX = 24;
/** Floor on usable page height, so an absurd inset cannot starve the page. */
const MIN_USABLE_PAGE_PX = 96;

export interface Box {
  top: number;
  bottom: number;
  height: number;
}

/** A heading, plus the point up to which it must stay glued to what follows. */
export interface GluedHeading {
  top: number;
  glueEnd: number;
}

export interface PageGeometry {
  /** Boxes that must not be split across pages (`break-inside: avoid`). */
  atomicBlocks: Box[];
  /** Headings that must not be stranded at a page foot (`break-after: avoid`). */
  gluedHeadings: GluedHeading[];
  /** One rect per rendered line of text, used to avoid slicing through glyphs. */
  lineRects: Box[];
  /** Bottom of the last rendered line — the real end of the document. */
  contentBottom: number;
}

/**
 * Read the page-break policy back off the rendered document.
 *
 * The template declares the policy in CSS; the browser resolves it; we ask the
 * resolved values rather than re-deriving them from class names. That is what
 * keeps this in step with the PDF export, which is fragmented by the same
 * declarations, and what lets a new template work without touching this file.
 */
function collectPageGeometry(doc: Document): PageGeometry {
  const view = doc.defaultView;
  const body = doc.body;
  if (!view || !body) {
    return { atomicBlocks: [], gluedHeadings: [], lineRects: [], contentBottom: 0 };
  }

  const bodyTop = body.getBoundingClientRect().top;
  const atomicBlocks: Box[] = [];
  const gluedIndexes: number[] = [];
  const boxes: Box[] = [];

  const elements = Array.from(body.querySelectorAll<HTMLElement>('*'));
  for (const element of elements) {
    const rect = element.getBoundingClientRect();
    if (rect.height <= 0) continue;

    const box: Box = { top: rect.top - bodyTop, bottom: rect.bottom - bodyTop, height: rect.height };
    const style = view.getComputedStyle(element);

    if (style.breakInside === 'avoid') {
      atomicBlocks.push(box);
    }
    if (style.breakAfter === 'avoid') {
      gluedIndexes.push(boxes.length);
    }
    boxes.push(box);
  }

  // A heading is glued to the next thing that actually renders below it, so the
  // break must clear that whole span or fall above the heading entirely.
  const gluedHeadings: GluedHeading[] = gluedIndexes.map((index) => {
    const heading = boxes[index];
    const following = boxes.slice(index + 1).find((box) => box.top >= heading.bottom - PAGE_EPSILON_PX);
    return { top: heading.top, glueEnd: following ? following.top : heading.bottom };
  });

  const lineRects = collectLineRects(doc, bodyTop);
  const contentBottom = lineRects.reduce((max, rect) => Math.max(max, rect.bottom), 0);

  return { atomicBlocks, gluedHeadings, lineRects, contentBottom };
}

/**
 * One rect per rendered line, obtained by ranging over text nodes. A block
 * element yields a single rect covering all its lines, which is too coarse to
 * break against — `Range.getClientRects()` gives us the actual line boxes.
 */
function collectLineRects(doc: Document, bodyTop: number): Box[] {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const range = doc.createRange();
  const rects: Box[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.nodeValue?.trim()) continue;
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) {
      if (rect.height <= 0) continue;
      rects.push({ top: rect.top - bodyTop, bottom: rect.bottom - bodyTop, height: rect.height });
    }
  }

  return rects.sort((a, b) => a.top - b.top);
}

/**
 * Move a page break upwards until it lands somewhere legal: not through an
 * unsplittable block, not through a line of text, and not immediately below a
 * heading. Each rule can expose another, so this runs to a fixed point.
 */
export function legalizeBreakOffset(candidate: number, geometry: PageGeometry, pageHeightPx: number): number {
  let offset = candidate;

  for (let pass = 0; pass < MAX_LEGALIZE_PASSES; pass += 1) {
    const splitBlocks = geometry.atomicBlocks.filter(
      (block) =>
        block.height < pageHeightPx - PAGE_EPSILON_PX &&
        block.top < offset - PAGE_EPSILON_PX &&
        block.bottom > offset + PAGE_EPSILON_PX
    );
    if (splitBlocks.length > 0) {
      offset = Math.min(...splitBlocks.map((block) => block.top));
      continue;
    }

    const splitLines = geometry.lineRects.filter(
      (rect) => rect.top < offset - PAGE_EPSILON_PX && rect.bottom > offset + PAGE_EPSILON_PX
    );
    if (splitLines.length > 0) {
      offset = Math.min(...splitLines.map((rect) => rect.top));
      continue;
    }

    const strandedHeading = geometry.gluedHeadings.find(
      (heading) => offset > heading.top && offset <= heading.glueEnd
    );
    if (strandedHeading) {
      offset = strandedHeading.top;
      continue;
    }

    return offset;
  }

  return offset;
}

/**
 * Vertical breathing room repeated at the top and bottom of every page, in px.
 *
 * Edge-to-edge templates print with no page margin so their background can
 * bleed, which leaves nothing to inset the text: pane padding applies only once
 * at the start and end of the flow, so continuation pages begin hard against
 * the paper edge. Such a template clones its pane decoration onto every
 * fragment for print and declares the amount as `--resume-page-inset-y`; this
 * reads it back so the preview reproduces the identical inset.
 *
 * Templates that print with a real page margin declare nothing and get 0 — the
 * margin already does the work.
 */
export function readPageInsetY(doc: Document): number {
  const view = doc.defaultView;
  if (!view || !doc.documentElement) return 0;

  const declared = view.getComputedStyle(doc.documentElement).getPropertyValue('--resume-page-inset-y').trim();
  if (!declared) return 0;

  // Resolve whatever unit the template used (mm, px, …) against the document.
  const probe = doc.createElement('div');
  probe.style.cssText = `position:absolute;visibility:hidden;height:${declared}`;
  doc.body.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();

  return Number.isFinite(px) && px > 0 ? px : 0;
}

export interface PageMetrics {
  /** Total height of real content, floored at one page. */
  contentHeightPx: number;
  /** Y offset in the rendered document at which each page's content begins. */
  pageOffsets: number[];
  /** Blank margin to leave above and below the content on every page. */
  pageInsetYPx: number;
  /** Content height available on one page, once the inset is taken off. */
  usableHeightPx: number;
}

/**
 * Split a rendered resume document into page offsets.
 *
 * The preview slices one tall render into A4 windows starting at these offsets,
 * which reproduces the fragmentation Chrome performs natively when the same
 * document is printed to PDF.
 */
export function computePageOffsets(doc: Document, pageHeightPx: number): PageMetrics {
  const body = doc.body;
  const docEl = doc.documentElement;
  const renderedHeightPx = Math.max(
    body?.scrollHeight ?? 0,
    body?.offsetHeight ?? 0,
    docEl?.scrollHeight ?? 0,
    docEl?.offsetHeight ?? 0
  );

  const pageInsetYPx = readPageInsetY(doc);
  const usableHeightPx = Math.max(pageHeightPx - pageInsetYPx * 2, MIN_USABLE_PAGE_PX);

  // The document already carries the inset once, as the leading pane padding,
  // so the first page starts there rather than at zero.
  const metrics = paginateGeometry(collectPageGeometry(doc), usableHeightPx, renderedHeightPx, pageInsetYPx);

  return { ...metrics, pageInsetYPx, usableHeightPx };
}

/**
 * The paging decision itself, over already-measured geometry. Split out from the
 * DOM read so it can be exercised without a layout engine.
 */
export function paginateGeometry(
  geometry: PageGeometry,
  pageHeightPx: number,
  renderedHeightPx: number,
  startOffsetPx = 0
): Pick<PageMetrics, 'contentHeightPx' | 'pageOffsets'> {
  // Trailing container padding is not content; page off the last line of text so
  // the document does not gain a near-empty final page.
  const effectiveHeight = geometry.contentBottom > 0 ? geometry.contentBottom : renderedHeightPx;
  const contentHeightPx = Math.max(effectiveHeight, startOffsetPx + pageHeightPx);

  const pageOffsets: number[] = [startOffsetPx];
  let currentOffset = startOffsetPx;

  while (currentOffset + pageHeightPx < contentHeightPx - PAGE_EPSILON_PX) {
    const naturalBreak = currentOffset + pageHeightPx;
    let nextOffset = legalizeBreakOffset(naturalBreak, geometry, pageHeightPx);

    // Nothing legal above the cut without losing the page entirely — take the
    // straight cut rather than stall.
    if (nextOffset <= currentOffset + MIN_BREAK_ADVANCE_PX) {
      nextOffset = naturalBreak;
    }

    if (contentHeightPx - nextOffset < MIN_LAST_PAGE_CONTENT_PX) break;
    if (nextOffset >= contentHeightPx - PAGE_EPSILON_PX) break;

    pageOffsets.push(nextOffset);
    currentOffset = nextOffset;
  }

  return { contentHeightPx, pageOffsets };
}
